import { inject, Injectable, signal, computed } from '@angular/core';
import { StorageService } from '../../../shared/services/storage.service';
import { StorageKey } from '@shared/enums/storage-keys.enum';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';

import { Observable, map, catchError, of } from 'rxjs';
import { JwtService } from '@app/core/services/jwt.service';
import { ToastService } from '@shared/components/toast';
import { Role } from '@shared/enums/role.enum';
import { User } from '@app/core/models/user';
import { EventManager, ObserverEvents } from '@app/core/observer';
import {
  AuthStatus,
  RegisterParams,
  LoginParams,
  DecodedToken,
} from './auth.interfaces';
import { AUTH_RELATED_STORAGE_KEYS } from './auth.constants';
import { AuthNavigationService } from '@domains/auth/auth-navigation.service';
import { PortalTypes } from '@shared/enums';

interface JwtResponse {
  access: string;
  refresh: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private storage = inject(StorageService);
  private jwtService = inject(JwtService);
  private toast = inject(ToastService);
  private eventManager = inject(EventManager);
  private authNavigationService = inject(AuthNavigationService);

  private _authStatus = signal<AuthStatus>('loading');
  private _roles = signal<Role[]>(this.storage.read(StorageKey.roles) ?? []);

  private _access = signal<string | undefined>(
    this.storage.read(StorageKey.access),
  );
  private _refresh = signal<string | undefined>(
    this.storage.read(StorageKey.refresh),
  );

  authStatus = computed(() => this._authStatus());
  access = computed(() => this._access());
  refresh = computed(() => this._refresh());
  isAuthenticated = computed(() => this.authStatus() === 'authenticated');
  roles = computed(() => this._roles());
  sessionUser = signal<User | null>(
    this.storage.read(StorageKey.sessionUser) ?? null,
  );
  public isClerk = computed(() => this.roles().includes(Role.Clerk));

  public register(registerParams: RegisterParams): Observable<null> {
    return this.http.post<null>(
      `${environment.api}/auth/register/`,
      registerParams,
    );
  }

  login(loginParams: LoginParams): Observable<boolean> {
    return this.http
      .post<JwtResponse>(`${environment.api}/auth/token/`, loginParams, {
        params: {
          role: loginParams.role ?? '',
        },
      })
      .pipe(
        map(({ access, refresh }) =>
          this.handleAuthSuccess({ access, refresh }),
        ),
        catchError(() => of(false)),
      );
  }

  getSessionUser(): Observable<User | null> {
    return this.http.get<User>(`${environment.api}/auth/me/`).pipe(
      map((user) => {
        this.storage.save(StorageKey.sessionUser, user);
        this.setSessionUserRoles(user.roles);
        this.sessionUser.set(user);
        return user;
      }),
      catchError(() => of(null)),
    );
  }

  verifyAuth(): Observable<boolean> {
    const access = this.storage.read(StorageKey.access);
    const refresh = this.storage.read(StorageKey.refresh);

    if (!access || !refresh) return this.handleAuthFailure();

    return this.http
      .post<boolean>(`${environment.api}/auth/token/verify/`, { token: access })
      .pipe(
        map(() => this.handleAuthSuccess({ access, refresh })),
        catchError(() => this.handleAuthFailure()),
      );
  }

  logout() {
    const userRoles = this._roles();

    this._authStatus.set('unauthenticated');
    this._access.set('');
    this._refresh.set('');
    this._roles.set([]);
    this.setSessionUserRoles([]);
    this._resetUserSessionFromStorage();
    this.eventManager.notify(ObserverEvents.LOGOUT);

    this.authNavigationService.goToAccessPage({
      portalType: userRoles.includes(Role.Clerk)
        ? PortalTypes.CLERK
        : PortalTypes.PUBLIC,
    });
  }

  _resetUserSessionFromStorage() {
    AUTH_RELATED_STORAGE_KEYS.forEach((key) => {
      this.storage.remove(key);
    });
  }

  handleAuthSuccess({ access, refresh }: JwtResponse) {
    this._authStatus.set('authenticated');
    this._access.set(access);
    this._refresh.set(refresh);
    this.storage.save(StorageKey.access, access);
    this.storage.save(StorageKey.refresh, refresh);

    try {
      const decodedToken = this._decodeTokenPayload(access);
      this.setSessionUserRoles(decodedToken.roles);
    } catch {
      this.handleAuthFailure();
      this.toast.show({
        message: 'An error ocurred retrieving credentials',
        type: 'error',
      });
    }

    return true;
  }

  setSessionUserRoles(roles: Role[]) {
    this._roles.set(roles);

    this.storage.save(StorageKey.roles, roles);
  }

  _decodeTokenPayload(access: string) {
    const decodedToken = this.jwtService.decodeToken<DecodedToken>(access);

    if (!decodedToken?.roles) {
      throw new Error('Roles not found in token');
    }
    return decodedToken;
  }

  handleAuthFailure() {
    this.logout();
    return of(false);
  }

  hasRole(requiredRoles: Role[]): boolean {
    return requiredRoles.some((role) => this._roles().includes(role));
  }
}
